import React, { useState, useEffect } from 'react';
import { Plus, X, Repeat, CornerUpLeft, CreditCard, Scissors, Send, Check } from 'react-feather';
import { apiClient } from '../shared/api/client';
import { useMonth } from '../contexts/MonthContext';
import { useSearchParams } from 'react-router-dom';
import { Transaction, REIMBURSABLE_CATEGORIES } from '../shared/types';
import PageHeader from '../components/PageHeader';
import CategorizeModal from '../components/CategorizeModal';

// The month's transactions as a day-grouped timeline. Click a transaction
// to categorize, split or tag it. Future months show predicted charges.
const TransactionsPage: React.FC = () => {
  const { currentMonth } = useMonth();
  const [searchParams, setSearchParams] = useSearchParams();
  const onlyUncategorized = searchParams.get('saknar') === 'kategori';
  // Unsubmitted work expenses span months (a June charge can still be
  // unclaimed in August), so this view deliberately ignores the month picker.
  const onlyUnsubmitted = searchParams.get('utlagg') === 'oskickade';
  const [allTxs, setAllTxs] = useState<Transaction[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [categorizeTx, setCategorizeTx] = useState<Transaction | null>(null);
  const [refundQuestions, setRefundQuestions] = useState<{ income: Transaction; expense: Transaction }[]>([]);

  // High-precision refund detection: an unlinked income with the exact
  // same amount AND the same merchant as an earlier expense is almost
  // certainly its return - surface it as a question instead of waiting
  // for the user to find it
  const normalizeName = (s: string) =>
    s.replace(/^K\*/i, '').replace(/[^a-zA-Z0-9åäöÅÄÖ]/g, '').toUpperCase().slice(0, 6);
  const computeRefundQuestions = (all: Transaction[]) => {
    let dismissed: string[] = [];
    try { dismissed = JSON.parse(localStorage.getItem('dismissedRefundPairs') || '[]'); } catch { dismissed = []; }
    const expenses = all.filter(t => t.type === 'EXPENSE' && !t.isTransfer);
    const out: { income: Transaction; expense: Transaction }[] = [];
    for (const income of all.filter(t => t.type === 'INCOME' && !t.isTransfer && !t.refundsTransactionId)) {
      // Same merchant, refund not larger than the purchase, and at most
      // 100 kr smaller (a typical return shipping fee) - exact matches win
      const candidates = expenses.filter(e => {
        const diff = Math.abs(e.amount) - Math.abs(income.amount);
        return diff >= -0.01 && diff <= 100 &&
          e.date <= income.date &&
          (new Date(income.date).getTime() - new Date(e.date).getTime()) < 90 * 86400000 &&
          normalizeName(e.rawDescription || e.description) === normalizeName(income.rawDescription || income.description);
      });
      const match = candidates.sort((a, b) =>
        (Math.abs(a.amount) - Math.abs(income.amount)) - (Math.abs(b.amount) - Math.abs(income.amount))
      )[0];
      if (!match) continue;
      if (dismissed.includes(`${income.id}-${match.id}`)) continue;
      out.push({ income, expense: match });
    }
    return out.slice(0, 3);
  };

  const acceptRefundQuestion = async (q: { income: Transaction; expense: Transaction }) => {
    try {
      await apiClient.put(`/transactions/${q.income.id}`, { refundsTransactionId: q.expense.id });
      setNotice(`Returen kvittades mot ${q.expense.description}.`);
      await loadTransactions();
    } catch (err) {
      console.error('Failed to link suggested refund:', err);
    }
  };

  const dismissRefundQuestion = (q: { income: Transaction; expense: Transaction }) => {
    let dismissed: string[] = [];
    try { dismissed = JSON.parse(localStorage.getItem('dismissedRefundPairs') || '[]'); } catch { dismissed = []; }
    dismissed.push(`${q.income.id}-${q.expense.id}`);
    localStorage.setItem('dismissedRefundPairs', JSON.stringify(dismissed));
    setRefundQuestions(prev => prev.filter(p => !(p.income.id === q.income.id && p.expense.id === q.expense.id)));
  };

  const isFutureMonth = () => {
    const now = new Date();
    const current = new Date(now.getFullYear(), now.getMonth(), 1);
    const selected = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    return selected > current;
  };

  const loadTransactions = async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;

      if (isFutureMonth()) {
        const response = await apiClient.get(`/forecast?year=${year}&month=${month}`);
        const forecastArray = (response as any).forecast || [];
        setTransactions(forecastArray.map((tx: any) => ({ ...tx, isPredicted: true })));
      } else {
        // The month's rows come from the server (a local filter over "the
        // latest 500" hides older months as history grows). Refund matching
        // still needs a wider window, so it gets its own recent slice.
        const [monthResponse, recentResponse] = await Promise.all([
          apiClient.get(`/transactions?year=${year}&month=${month}&limit=500`),
          apiClient.get('/transactions?limit=500'),
        ]);
        setTransactions((monthResponse as any).transactions || []);
        setAllTxs((recentResponse as any).transactions || []);
        setRefundQuestions(computeRefundQuestions((recentResponse as any).transactions || []));
      }
    } catch (error) {
      console.error('Load transactions error:', error);
      setTransactions([]);
    }
  };

  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount) + ' kr';

  // Timeline labels: "Måndag" + "20 juli" (year appended when it differs)
  const weekdayOf = (dateStr?: string) => {
    if (!dateStr) return 'Okänt';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'Okänt';
    const w = d.toLocaleDateString('sv-SE', { weekday: 'long' });
    return w.charAt(0).toUpperCase() + w.slice(1);
  };
  const dayMonthOf = (dateStr?: string) => {
    if (!dateStr) return 'datum';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'datum';
    const label = d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'long' });
    return d.getFullYear() !== new Date().getFullYear() ? `${label} ${d.getFullYear()}` : label;
  };

  // Group the (date-sorted) transaction list into one bucket per day
  const groupByDate = (txs: Transaction[]) => {
    const groups: { date?: string; txs: Transaction[] }[] = [];
    for (const tx of txs) {
      const last = groups[groups.length - 1];
      if (last && last.date === tx.date) last.txs.push(tx);
      else groups.push({ date: tx.date, txs: [tx] });
    }
    return groups;
  };

  const openCategorize = (tx: Transaction) => {
    if (tx.isPredicted || tx.isTransfer) return;
    setCategorizeTx(tx);
  };

  const isWorkExpense = (t: Transaction) =>
    t.type === 'EXPENSE' && !t.isTransfer && !!t.category
    && REIMBURSABLE_CATEGORIES.includes(t.category.name);
  const unsubmitted = allTxs.filter(t => isWorkExpense(t) && !t.reimbursementSubmittedAt);
  const unsubmittedTotal = unsubmitted.reduce((s, t) => s + Math.abs(t.amount), 0);

  const markSubmitted = async () => {
    if (unsubmitted.length === 0) return;
    try {
      setSubmitting(true);
      await apiClient.post('/transactions/reimbursements/submit', { ids: unsubmitted.map(t => t.id) });
      await loadTransactions();
    } catch (error) {
      console.error('Mark submitted error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const visibleTransactions = onlyUnsubmitted
    ? unsubmitted
    : onlyUncategorized
      ? transactions.filter(t => !t.category && !(t.splits || []).length && !t.isTransfer)
      : transactions;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader mode="month" />
      <div className="page-content px-4 py-6">
        <div className="space-y-3">
          {notice && (
            <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-lg flex justify-between items-center">
              <span className="text-green-800 text-sm">{notice}</span>
              <button onClick={() => setNotice(null)} className="text-green-600 ml-3"><X size={14} /></button>
            </div>
          )}
          {refundQuestions.map(q => (
            <div key={`${q.income.id}-${q.expense.id}`} className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-blue-900 min-w-0 flex items-center">
                <CornerUpLeft size={14} className="mr-2 flex-shrink-0" />
                <span>
                  Är <strong>+{formatAmount(Math.abs(q.income.amount))} {q.income.description}</strong>
                  {q.income.date && ` (${new Date(q.income.date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })})`}
                  {' '}en retur på köpet <strong>{q.expense.description} · {formatAmount(Math.abs(q.expense.amount))}</strong>
                  {q.expense.date && ` (${new Date(q.expense.date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })})`}?
                  {Math.abs(q.expense.amount) - Math.abs(q.income.amount) > 0.01 &&
                    ` Mellanskillnad ${formatAmount(Math.abs(q.expense.amount) - Math.abs(q.income.amount))} — frakt?`}
                </span>
              </p>
              <span className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => acceptRefundQuestion(q)}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                >
                  Kvitta
                </button>
                <button
                  onClick={() => dismissRefundQuestion(q)}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg"
                >
                  Nej
                </button>
              </span>
            </div>
          ))}
          {/* Work expenses waiting to be claimed. Shown as its own view
              because the list crosses months - the month picker would hide
              exactly the old charges that are easiest to forget. */}
          {onlyUnsubmitted && (
            <div className="bg-white border border-amber-200 rounded-xl p-4 mb-3">
              <p className="text-sm text-gray-500">Utlägg som inte skickats till arbetsgivaren</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{formatAmount(unsubmittedTotal)}</p>
              <p className="text-xs text-gray-500 mt-1">{unsubmitted.length} utlägg, oavsett månad</p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={markSubmitted}
                  disabled={submitting || unsubmitted.length === 0}
                  className="px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white disabled:opacity-50"
                >
                  <Send size={14} className="inline mr-1 -mt-0.5" />
                  {submitting ? 'Markerar…' : 'Markera alla som inskickade'}
                </button>
                <button
                  onClick={() => { const p = new URLSearchParams(searchParams); p.delete('utlagg'); setSearchParams(p, { replace: true }); }}
                  className="px-3 py-2 text-sm font-medium rounded-lg bg-white text-gray-600 border border-gray-200"
                >
                  Visa månaden igen
                </button>
              </div>
            </div>
          )}
          {!onlyUnsubmitted && unsubmitted.length > 0 && (
            <button
              onClick={() => { const p = new URLSearchParams(searchParams); p.set('utlagg', 'oskickade'); setSearchParams(p, { replace: true }); }}
              className="w-full text-left bg-white border border-gray-200 rounded-xl px-4 py-3 mb-3"
            >
              <span className="text-sm text-gray-600">
                <Send size={14} className="inline mr-2 -mt-0.5 text-amber-600" />
                {formatAmount(unsubmittedTotal)} i utlägg är inte inskickade
              </span>
            </button>
          )}
          {(() => {
            // A transaction counts as uncategorized when nothing places it in
            // a bucket: no category, no split parts, not an internal transfer
            // and not netted against another transaction (a refund belongs to
            // the category of the purchase it cancels out).
            const missing = transactions.filter(t => !t.category && !(t.splits || []).length && !t.isTransfer && !t.refundsTransactionId);
            if (missing.length === 0 && !onlyUncategorized) return null;
            return (
              <div className="flex gap-2">
                <button
                  onClick={() => { const p = new URLSearchParams(searchParams); p.delete('saknar'); setSearchParams(p, { replace: true }); }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-full border ${!onlyUncategorized ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'}`}
                >
                  Alla ({transactions.length})
                </button>
                <button
                  onClick={() => { const p = new URLSearchParams(searchParams); p.set('saknar', 'kategori'); setSearchParams(p, { replace: true }); }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-full border ${onlyUncategorized ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'}`}
                >
                  Saknar kategori ({missing.length})
                </button>
              </div>
            );
          })()}
          {visibleTransactions.length > 0 ? (
            groupByDate(visibleTransactions).map(group => (
              <div key={group.date || 'unknown'} className="flex">
                <div className="w-24 flex-shrink-0 pr-4 text-right">
                  <div className="sticky top-[64px]">
                    <p className="text-sm font-semibold text-gray-700 leading-tight">{weekdayOf(group.date)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{dayMonthOf(group.date)}</p>
                  </div>
                </div>
                <div className="relative flex-1 border-l-2 border-gray-200 pl-4 pb-6 space-y-3 min-w-0">
                  <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-gray-300"></span>
                  {group.txs.map(tx => {
                    // Fully refunded purchases and consumed returns are
                    // settled - struck through so they read as neutralized
                    const settled = !tx.isPredicted && (
                      (tx.type === 'EXPENSE' && (tx.refundTotal || 0) >= Math.abs(tx.amount) - 0.01 && (tx.refundTotal || 0) > 0) ||
                      (tx.type === 'INCOME' && !!tx.refundsTransactionId)
                    );
                    return (
                    <div
                      key={tx.id}
                      onClick={() => openCategorize(tx)}
                      className={`bg-white rounded-xl p-4 shadow-sm ${tx.isPredicted ? 'border-2 border-dashed border-blue-300' : ''} ${!tx.isPredicted && !tx.isTransfer ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''} ${settled ? 'opacity-60' : ''}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className={`text-base font-semibold ${settled ? 'line-through text-gray-400' : 'text-gray-900'}`}>{tx.description}</h3>
                            {tx.isPredicted && (
                              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                                Förutsedd
                              </span>
                            )}
                            {tx.isTransfer && (
                              <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full inline-flex items-center">
                                <Repeat size={10} className="mr-1" />Överföring
                              </span>
                            )}
                            {tx.category && REIMBURSABLE_CATEGORIES.includes(tx.category.name) && tx.type === 'EXPENSE' && (
                              <span className="bg-amber-100 text-amber-800 text-xs font-medium px-2 py-0.5 rounded-full inline-flex items-center">
                                <CornerUpLeft size={10} className="mr-1" />Kvittas
                              </span>
                            )}
                            {(tx.splits || []).length > 0 && (
                              <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-0.5 rounded-full inline-flex items-center">
                                <Scissors size={10} className="mr-1" />Delad
                              </span>
                            )}
                            {tx.reimbursementSubmittedAt && (
                              <span className="text-xs text-green-700 bg-green-50 border border-green-100 rounded px-1.5 py-0.5 mr-1">
                                <Check size={11} className="inline -mt-0.5 mr-0.5" />Inskickat
                              </span>
                            )}
                            {tx.refundsTransactionId && (
                              <span className="bg-green-50 border border-green-200 text-green-700 text-xs font-medium px-2 py-0.5 rounded-full inline-flex items-center">
                                <CornerUpLeft size={10} className="mr-1" />Retur för {tx.refundsTargetName}
                              </span>
                            )}
                          </div>
                          {(tx.splits || []).length > 0 ? (
                            <span className="inline-flex flex-wrap items-center gap-1 mt-1">
                              {tx.splits!.map(s => (
                                <span key={s.id} className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.category.color || '#9CA3AF' }}></span>
                                  {s.category.name}: {formatAmount(s.amount)}
                                </span>
                              ))}
                            </span>
                          ) : tx.category ? (
                            <span className="inline-flex items-center gap-1 mt-1 text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tx.category.color || '#9CA3AF' }}></span>
                              {tx.category.name}
                            </span>
                          ) : (!tx.isPredicted && !tx.isTransfer && (
                            <span className="inline-flex items-center mt-1 text-xs text-blue-600"><Plus size={12} className="mr-0.5" />Kategorisera</span>
                          ))}
                          {(tx.tags || []).length > 0 && (
                            <span className="inline-flex flex-wrap items-center gap-1 mt-1 ml-1">
                              {tx.tags!.map(tag => (
                                <span
                                  key={tag.id}
                                  className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full text-white"
                                  style={{ backgroundColor: tag.color || '#0EA5E9' }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          {settled && tx.type === 'EXPENSE' ? (
                            <>
                              <span className="text-lg font-semibold line-through text-gray-400">
                                -{formatAmount(Math.abs(tx.amount))}
                              </span>
                              <p className="text-xs text-gray-400 mt-1">helt returnerat</p>
                            </>
                          ) : settled && tx.type === 'INCOME' ? (
                            <>
                              <span className="text-lg font-semibold line-through text-gray-400">
                                +{formatAmount(Math.abs(tx.amount))}
                              </span>
                              <p className="text-xs text-gray-400 mt-1">kvittad retur</p>
                            </>
                          ) : tx.type === 'EXPENSE' && (tx.refundTotal || 0) > 0 ? (
                            <>
                              <span className="text-lg font-semibold text-red-600">
                                -{formatAmount(Math.abs(tx.amount) - (tx.refundTotal || 0))}
                              </span>
                              <p className="text-xs text-gray-400 mt-1">
                                {formatAmount(Math.abs(tx.amount))} − {formatAmount(tx.refundTotal || 0)} retur
                              </p>
                            </>
                          ) : (
                            <>
                              <span className={`text-lg font-semibold ${
                                tx.type === 'INCOME'
                                  ? 'text-green-600'
                                  : tx.type === 'EXPENSE'
                                  ? 'text-red-600'
                                  : 'text-gray-600'
                              }`}>
                                {tx.type === 'INCOME' ? '+' : tx.type === 'EXPENSE' ? '-' : ''}
                                {formatAmount(Math.abs(tx.amount))}
                              </span>
                              <p className="text-xs text-gray-500 mt-1">
                                {tx.type === 'INCOME' ? 'Inkomst' : tx.type === 'EXPENSE' ? 'Utgift' : 'Överföring'}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <CreditCard size={48} className="mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {onlyUncategorized ? 'Allt är kategoriserat' : isFutureMonth() ? 'Inga förutsedda transaktioner' : 'Inga transaktioner denna månad'}
              </h3>
              <p className="text-gray-600">
                {isFutureMonth()
                  ? 'Skapa återkommande regler för att förutsäga framtida utgifter'
                  : 'Transaktioner kommer att visas här när de importeras från din bank'}
              </p>
            </div>
          )}
        </div>
      </div>

      {categorizeTx && (
        <CategorizeModal
          tx={categorizeTx}
          onClose={() => setCategorizeTx(null)}
          onSaved={async (message) => {
            setCategorizeTx(null);
            setNotice(message);
            await loadTransactions();
          }}
          onDataChanged={() => loadTransactions()}
        />
      )}
    </div>
  );
};

export default TransactionsPage;
